import React, {useRef, useState, useEffect, useLayoutEffect} from "react";
import DragAndDrop from "../helpers/DragAndDrop";
import SplitPane from "react-split-pane";
import BrowseGrid from "./BrowseGrid";
import {AssetDragLayer} from "./assetDragHelpers";
import ExploreLeftSideBar from "./ExploreLeftSideBar";
import {SelectedAssetsProvider, useSelectedAssetsState} from "../../contexts/SelectedAssetsContext";
import {SelectedAggsProvider} from "../../contexts/SelectedAggsContext";
import ExploreRightSideBar from "./ExploreRightSideBar";
import {useAssetGroupState} from "../../contexts/AssetGroupContext";

import {Badge, Button, Drawer, Layout, message, Typography, theme} from 'antd'

import {
    BrowserView,
    MobileView,
    isBrowser,
    isMobile
} from "device-detect";
import {CheckOutlined, CompassOutlined, MenuOutlined} from "@ant-design/icons";
import SiteSearch from "../widgets/SiteSearch";
import {LightboxIcon} from "../helpers/icons";
import SettingOutlined from "@ant-design/icons/lib/icons/SettingOutlined";
import {useFilters} from "../helpers/useFilters";
import {GlobalHotKeys} from "react-hotkeys";
import useCurrentUser from "../helpers/useCurrentUser";
import {useNavigate, useLocation} from "@/components/helpers/routerCompat";
import DoubleRightOutlined from "@ant-design/icons/lib/icons/DoubleRightOutlined";
import {useTranslation} from "react-i18next";
import {useViewMode} from './ViewMenu';
import useNarrowViewport, {useViewportWidth} from '../helpers/useNarrowViewport';
import WorkspaceStatus from './WorkspaceStatus';
import {useTourDrawerNavigation} from './exploreTourNavigation';
import useHeaderHeight from '../helpers/useHeaderHeight';

export default ()=> {
    const {t} = useTranslation()
    const {currentAssetGroup, currentUpload, viewAllType} = useAssetGroupState()
    const currentUser = useCurrentUser()
    const {viewMode} = useViewMode()
    const narrow = useNarrowViewport();
    const compact = isMobile() || narrow;
    const viewportWidth = useViewportWidth();

    const sizeKey = `ExploreLeftSplit-${isMobile() ? 'mobile' : 'desktop'}`
    const leftSize = useRef(
        parseInt(sessionStorage.getItem(sizeKey), 10) ||
        (isMobile() ? window.innerWidth : 375)
    );
    const [leftWidth, setLeftWidth] = useState(leftSize.current);

    const leftChange = size => {
        sessionStorage.setItem(sizeKey, size.toString())
        leftSize.current = size
        setLeftWidth(size)
    }

    const [leftSidebarCollapsed, setLeftSidebarCollapsed] = useState(sessionStorage.getItem('ExploreLeftCollapsed') === 'true' || !currentUser || currentUpload);

    const toggleLeftSidebar = ()=> {
        const value = !leftSidebarCollapsed
        sessionStorage.setItem('ExploreLeftCollapsed', value)
        setLeftSidebarCollapsed(value)
    }

    const headerHeight = useHeaderHeight();

    const [rightDrawerVisible, setRightDrawerVisible] = useState()
    const leftPaneSize = leftSidebarCollapsed ? 40 : Math.max(40, Math.min(leftWidth, viewportWidth - 640));
    const location = useLocation()

    useEffect(() => {
        setRightDrawerVisible(false)
    }, [location.pathname, currentAssetGroup?.id, currentUpload?.id, viewAllType])

    const showRight =
        (viewMode === 'grid' || viewMode === 'masonry' || currentAssetGroup?.commentable ||
            !!currentAssetGroup?.workflow_steps?.filter(s => !s.auto_approval )?.length ||
            !!currentAssetGroup?.workflow_steps?.filter(s => s.send_to_lightroom && s.lightroom_user_id === currentUser?.id )?.length
        ) && currentUser

    const ref = useRef();
    const keyMap = { BRACKET_LEFT: '[' }

    const handlers = {
        BRACKET_LEFT: (e) => {
            e.preventDefault()
            toggleLeftSidebar()
        }
    }

    return (
        (<GlobalHotKeys keyMap={keyMap} handlers={handlers} innerRef={ref} allowChanges>
            <DragAndDrop>
                <AssetDragLayer/>
                <WorkspaceStatus/>
                <div style={{position:'relative', width:'100%', overflow: 'auto', height:`calc(100vh - ${headerHeight}px)`}}>
                    {!compact && <>
                        <SplitPane
                            split='vertical'
                            pane1Style={{overflow: leftSidebarCollapsed ? 'visible' : 'auto'}}
                            size={leftPaneSize}
                            onChange={leftChange}
                            onDragStarted={()=> { if(leftSidebarCollapsed) toggleLeftSidebar() }}
                        >
                            <ExploreLeftSideBar collapsed={leftSidebarCollapsed} toggleSidebar={toggleLeftSidebar}/>
                            <CenterPane showRight={showRight} availableWidth={viewportWidth - leftPaneSize}/>
                        </SplitPane>
                    </>}

                    {compact && <>
                        <MobileExploreDrawer/>

                        {showRight && (
                            <MobileRightDrawer open={rightDrawerVisible} setOpen={setRightDrawerVisible}/>
                        )}
                    </>}
                </div>
            </DragAndDrop>
        </GlobalHotKeys>)
    );
}

// Mobile stand-in for the right sidebar (workflow approvals, comments, Lightroom).
const MobileRightDrawer = ({open, setOpen})=>{
    const {t} = useTranslation();
    const {token} = theme.useToken()
    const {currentAssetGroup} = useAssetGroupState()
    const {selectedAssetIds} = useSelectedAssetsState()
    const trigger = useRef();

    const isAccessRequest = !!currentAssetGroup?.access_request_id

    const label = isAccessRequest ?
        t('approve-or-reject','Approve or Reject') :
        t('tooltip-show-sidebar','Show Sidebar')

    return (<>
        <Drawer
            placement={'right'}
            onClose={() => setOpen(false)}
            open={open}
            width={'100vw'}
            // Request counts and the Pending filter must load before opening.
            forceRender={isAccessRequest}
            styles={{body: {padding: '0 .5em'}}}
            id={'explore-right-drawer'}
            afterOpenChange={visible => { if(!visible) trigger.current?.focus(); }}
            title={<><LightboxIcon/> {currentAssetGroup?.name || t('workspace', 'Workspace')}</>}
            extra={!!selectedAssetIds?.length && (
                <Badge count={selectedAssetIds.length} overflowCount={999} title={t('selected','Selected')}/>
            )}
        >
            <ExploreRightSideBar onNavigate={() => setOpen(false)}/>
        </Drawer>

        {(
            <Button
                ref={trigger}
                aria-expanded={!!open}
                // Bottom-left: the support chat widget owns the bottom-right corner.
                style={{position:'fixed', bottom: '1.5em', left: '1em', zIndex: 1000, visibility: open ? 'hidden' : undefined, boxShadow: token.boxShadowSecondary}}
                onClick={()=>setOpen(true)}
                type={'primary'}
                shape={'round'}
                size={'large'}
                icon={isAccessRequest ? <CheckOutlined/> : <SettingOutlined/>}
                id={'mobile-right-sidebar-toggle'}
                aria-label={label}
            >
                {label}
                {!!selectedAssetIds?.length && (
                    <Badge count={selectedAssetIds.length} overflowCount={999} color={token.colorBgContainer} style={{color:token.colorPrimary, marginLeft:'.25em'}}/>
                )}
            </Button>
        )}
    </>)
}

const MobileExploreDrawer = ({})=>{
    const {t} = useTranslation();
    const {token} = theme.useToken()
    const {open: drawerVisible, setOpen: setDrawerVisible, afterOpenChange: settleTourNavigation, closeOnScopeChange} = useTourDrawerNavigation();
    const trigger = useRef();

    const {currentAssetGroup, currentUpload, viewAllType} = useAssetGroupState()
    const {filters} = useFilters()

    useLayoutEffect(()=>{
        closeOnScopeChange()
    }, [currentUpload?.id, currentAssetGroup?.id, viewAllType, filters?.q, closeOnScopeChange])

    const inputRef = useRef()
    const location = useLocation()
    const navigate = useNavigate();

    useEffect(()=> {
        if (location.hash == '#search') {
            setDrawerVisible(true)
            setTimeout(()=>{
                inputRef.current?.focus()
                location.hash = null
                navigate(location)
            }, 200)
        }
    }, [location.hash])

    return (<>
        <Drawer
            placement={'left'}
            onClose={() => setDrawerVisible(false)}
            open={drawerVisible}
            width={'100vw'}
            bodyStyle={{padding: 0, margin: '.5em .5em 0 .5em'}}
            id={'explore-left-drawer'}
            afterOpenChange={visible => {
                settleTourNavigation(visible);
                if(!visible) (trigger.current || document.getElementById('main'))?.focus();
            }}
            title={<><CompassOutlined/> {t('explore-navigation', 'Explore Navigation')}</>}
        >
            <div style={{marginBottom: '1em'}}>
                <SiteSearch inputRef={inputRef}/>
            </div>

            <ExploreLeftSideBar/>
        </Drawer>
        {!currentUpload && (
            <Button
                ref={trigger}
                type={'text'}
                style={{position: 'absolute', top: 8, left: 0, zIndex: 1000, width: 32, height: 48, padding: 1, overflow: 'hidden', borderRight: `1px solid ${token.colorBorderSecondary}`, backgroundColor: token.colorFillSecondary}}
                onClick={() => setDrawerVisible(true)}
                tabIndex={0}
                id={'mobile-sidebar-toggle'}
                aria-label={t('toggle-left-hand-sidebar', 'Toggle Left-hand Sidebar')}
                aria-expanded={!!drawerVisible}
            >
                <Typography.Text type={'secondary'}>
                    <DoubleRightOutlined style={{marginTop: 18}}/>
                </Typography.Text>
            </Button>
        )}
        <div style={{position: 'absolute', height: '100%', width: '100%'}}>
            <BrowseGrid/>
        </div>
    </>);
}


const CenterPane = ({showRight, availableWidth}) => {
    const {currentAssetGroup} = useAssetGroupState()

    const rightSize = useRef(parseInt(sessionStorage.getItem('ExploreRightSplit'), 10) || 350);
    const rightChange = size => {
        sessionStorage.setItem('ExploreRightSplit', size.toString())
        rightSize.current = size
    }

    const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(() => sessionStorage.getItem('ExploreRightCollapsed') === 'true')

    const toggleRightSidebar = ()=> {
        const value = !rightSidebarCollapsed
        sessionStorage.setItem('ExploreRightCollapsed', String(value))
        setRightSidebarCollapsed(value)
    }

    const ref = useRef();
    const keyMap = { BRACKET_RIGHT: ']'}

    const handlers = {
        BRACKET_RIGHT: (e) => {
            if (!showRight) return
            e.preventDefault()
            toggleRightSidebar()
        },
    }

    return (
        <GlobalHotKeys keyMap={keyMap} handlers={handlers} innerRef={ref} allowChanges>
            <SplitPane
                split='vertical'
                size={!showRight ? 0 : rightSidebarCollapsed ? 15 : Math.min(rightSize.current, Math.max(15, availableWidth - 320))}
                allowResize={!!showRight}
                resizerStyle={!showRight ? {display: 'none'} : undefined}
                pane2Style={{display: showRight ? undefined : 'none', overflow: rightSidebarCollapsed ? 'visible' : 'auto'}}
                onChange={rightChange}
                primary={'second'}
            >
                <BrowseGrid/>
                {showRight ? <ExploreRightSideBar collapsed={rightSidebarCollapsed} toggleSidebar={toggleRightSidebar}/> : <div/>}
            </SplitPane>
        </GlobalHotKeys>
    )
}
